import React, { useRef, useState } from 'react'
import { accountBackupsAPI } from '@hpanel/web-common/api/client'
import type { RestoreJobV3 } from '@hpanel/web-common/api/client'
import { RestoreJobResult } from './RestoreJobResult'

interface Props {
  onRestored: () => void
  onError: (msg: string) => void
}

/**
 * Drag-drop / file-picker form for uploading a .tar.gz backup and
 * launching an async restore job.
 *
 * Flow:
 *   1. Tenant picks a v3 account .tar.gz (drag-drop or file input).
 *   2. On submit: POST multipart to /api/backups/account/restore → RestoreJob.
 *   3. RestoreJobResult polls GET /api/backups/restores/{id} until
 *      success | partial | failed and renders the report.
 *
 * data-testid attributes:
 *   backups-upload-input    — the hidden file <input>
 *   backups-upload-submit   — the Submit button
 *   backups-restore-progress — rendered by RestoreProgress while polling
 */
export const UploadBackupForm: React.FC<Props> = ({ onRestored, onError }) => {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [restoreId, setRestoreId] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const acceptFile = (f: File) => {
    if (!f.name.endsWith('.tar.gz') && !f.name.endsWith('.tgz')) {
      onError('Please select a .tar.gz backup file.')
      return
    }
    setFile(f)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) acceptFile(f)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return
    setUploading(true)
    try {
      const job = await accountBackupsAPI.restoreUpload(file)
      setRestoreId(job.id)
    } catch (err: unknown) {
      console.error('UploadBackupForm: upload restore failed', err)
      onError(err instanceof Error ? err.message : 'Upload failed')
      setUploading(false)
    }
    // uploading stays true; RestoreProgress will call onDone when finished
  }

  const handleRestoreDone = (job: RestoreJobV3) => {
    setUploading(false)
    if (job.status === 'success') onRestored()
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        Restore from backup file
      </h2>

      {restoreId !== null ? (
        <RestoreJobResult restoreId={restoreId} onFinished={handleRestoreDone} />
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {/* Drag-drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`
              flex flex-col items-center justify-center gap-2
              border-2 border-dashed rounded-lg p-6 cursor-pointer
              transition-colors text-sm
              ${isDragging
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }
            `}
          >
            <span className="text-gray-500 dark:text-gray-400">
              {file
                ? file.name
                : 'Drop a .tar.gz backup here, or click to browse'}
            </span>
            <input
              ref={inputRef}
              data-testid="backups-upload-input"
              type="file"
              accept=".tar.gz,.tgz"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) acceptFile(f)
              }}
            />
          </div>

          <button
            data-testid="backups-upload-submit"
            type="submit"
            disabled={!file || uploading}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm rounded-lg font-medium"
          >
            {uploading ? 'Uploading…' : 'Restore backup'}
          </button>
        </form>
      )}
    </div>
  )
}
